import { Box, Flex, LinkBox, LinkOverlay, Table, type TableCellProps, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { createContext, type ReactNode, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { KEYBOARD_SCROLL_FOCUS } from "../../../common/KeyboardScrollBox";
import { NARROW_ONLY, WIDE_ONLY_CELL } from "../../../common/responsiveTable";

const RowLink = createContext<string | null>(null);

export const LINKED_ROW_FOCUS = {
  "&:has(a:focus-visible)": KEYBOARD_SCROLL_FOCUS,
  "& a:focus-visible": { outline: "none", boxShadow: "none" },
};

/**
 * The shared shape of every home panel table: no wrapping, so rows stay one line high and the panel
 * scrolls sideways instead of growing tall, and a title column that truncates rather than pushing the
 * other columns out of sight.
 *
 * The floor on the table's width is what makes the panel's horizontal scroll appear on a narrow
 * screen. Without it the title column would collapse toward nothing to keep the table fitting, and
 * the row would read as an ellipsis with a date next to it.
 */
export const PanelTable = ({
  headers,
  phoneColumns,
  children,
}: {
  headers: string[];
  phoneColumns?: number;
  children: ReactNode;
}) => (
  <Table variant="simple" size="sm" whiteSpace="nowrap" minW={phoneColumns ? { base: "0", md: "28rem" } : "28rem"}>
    <Thead>
      <Tr>
        {headers.map((header, index) => (
          <Th key={header} display={phoneColumns !== undefined && index >= phoneColumns ? WIDE_ONLY_CELL : undefined}>
            {header}
          </Th>
        ))}
      </Tr>
    </Thead>
    <Tbody>{children}</Tbody>
  </Table>
);

export const PanelRow = ({ to, children }: { to: string; children: ReactNode }) => (
  <LinkBox as={Tr} cursor="pointer" _hover={{ bg: "smPrimarySofter" }} sx={{ transform: "scale(1)", ...LINKED_ROW_FOCUS }}>
    <RowLink.Provider value={to}>{children}</RowLink.Provider>
  </LinkBox>
);

const RowTitle = ({ children }: { children: ReactNode }) => {
  const to = useContext(RowLink);
  return to ? (
    <LinkOverlay as={RouterLink} to={to}>
      {children}
    </LinkOverlay>
  ) : (
    <>{children}</>
  );
};

/**
 * `maxW=0` with `w=100%` is what lets a cell in an auto-layout table shrink below its content width,
 * which is what makes the ellipsis appear at all. The column still takes whatever room the fixed-width
 * columns leave over.
 */
export const TitleCell = ({
  title,
  subtitle,
  subtitleColor,
  icon,
  isUnread,
  clampLines,
  wrapTitle,
  width = "100%",
  phoneDetail,
}: {
  title: ReactNode;
  subtitle?: string;
  subtitleColor?: string;
  icon?: ReactNode;
  isUnread?: boolean;
  /**
   * For titles that are whole sentences rather than names. A single-line ellipsis cannot cut a
   * composed description — `text-overflow` only reaches the element's own inline content, not the
   * markup a rendered description brings with it — so those wrap and clamp to this many lines.
   */
  clampLines?: number;
  wrapTitle?: boolean;
  width?: TableCellProps["w"];
  phoneDetail?: ReactNode;
}) => (
  <Td maxW="0" w={width} whiteSpace={clampLines || wrapTitle ? "normal" : undefined}>
    <Flex align="center" gap="3" minW="0">
      {/*
        Unread used to be a heavier weight, which made rows in the same table disagree about how bold
        text should be. The dot says the same thing without touching the type — and matches how the
        notification list marks unread. Kept in the layout when read so rows stay aligned.
      */}
      {isUnread !== undefined && (
        <Box
          w="6px"
          h="6px"
          rounded="full"
          flexShrink={0}
          bg="smPrimary"
          visibility={isUnread ? "visible" : "hidden"}
          data-testid={isUnread ? "panel-unread-dot" : undefined}
        />
      )}
      {icon}
      <Box minW="0" flex="1">
        {clampLines ? (
          <Box
            overflow="hidden"
            color="smMuted"
            sx={{ display: "-webkit-box", WebkitLineClamp: clampLines, WebkitBoxOrient: "vertical" }}
          >
            <RowTitle>{title}</RowTitle>
          </Box>
        ) : wrapTitle ? (
          <Text color="smMuted" overflowWrap="anywhere" data-testid="panel-wrapped-title">
            <RowTitle>{title}</RowTitle>
          </Text>
        ) : (
          <Tooltip label={typeof title === "string" ? title : undefined} openDelay={600}>
            <Text isTruncated color="smMuted">
              <RowTitle>{title}</RowTitle>
            </Text>
          </Tooltip>
        )}
        {subtitle && (
          <Text isTruncated fontSize="xs" color={subtitleColor ?? "smMuted"}>
            {subtitle}
          </Text>
        )}
        {phoneDetail && (
          <Box display={NARROW_ONLY} whiteSpace="normal" data-testid="panel-phone-detail">
            {phoneDetail}
          </Box>
        )}
      </Box>
    </Flex>
  </Td>
);

export const MutedCell = ({ children, ...cell }: { children: ReactNode } & TableCellProps) => (
  <Td {...cell}>
    <Text fontSize="sm" color="smMuted">
      {children}
    </Text>
  </Td>
);
